import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../redux/store";
import Main from "../../components/custom/Main";
import PageContainer from "../../components/custom/PageContainer";
import Modal from "../../components/Modal";
import CollapseCard from "../../components/custom/CollapseCard";
import { useToast } from "../../components/toast/ToastProvider";
import { StoreState } from "../../redux/reducers";
import { EmpReport } from "../../types/EmpReport";
import {
  addEmpReportAction,
  deleteEmpReportAction,
  editEmpReportAction,
  fetchEmpReportsAction,
} from "../../redux/actions/empReportAction";
import EmployeeReportForm from "../../components/EmployeeReportForm";
import EmployeeReportList from "../../components/EmployeeReportList";
import { fetchTasks } from "../../redux/actions/taskAction";
import { fetchProjects } from "../../redux/actions/projectAction";
import { fetchEmployeesAction } from "../../redux/actions/employeeActions";
import EmployeeReportFilter from "../../components/EmployeeReportFilter";

const EmployeeReportPageUser = () => {
  const dispatch = useDispatch<AppDispatch>();
  const toast = useToast();
  const empReportList = useSelector(
    (state: StoreState) => state.employeeReport,
  );
  const [selectedEmpReport, setSelectedEmpReport] = useState<EmpReport | null>(
    null,
  );
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const tasks = useSelector((state: StoreState) => state.task);

  useEffect(() => {
    setLoading(true);
    dispatch(fetchEmpReportsAction()).finally(() => setLoading(false));
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchProjects());
    dispatch(fetchTasks());
    dispatch(fetchEmployeesAction());
  }, [dispatch]);

  const handleEdit = (report: EmpReport) => {
    setSelectedEmpReport(report);
    setShowModal(true);
  };

  // --- FIX: Parameter name changed from 'EmpReport' to 'data' with proper type annotation ---
  const addOrUpdateHandler = async (data: EmpReport) => {
    setLoading(true);
    try {
      if (selectedEmpReport) {
        await dispatch(editEmpReportAction(data.id, data));
        toast("Employee Report updated successfully");
      } else {
        await dispatch(addEmpReportAction(data));
        toast("Employee Report added successfully");
      }
      await dispatch(fetchEmpReportsAction());
      setSelectedEmpReport(null);
      setShowModal(false);
    } catch (error: any) {
      console.error("EmpReport Save Error:", error);
      toast(error?.toString() || "Error in Save EmpReport");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("Are you sure?")) {
      setLoading(true);
      await dispatch(deleteEmpReportAction(id));
      toast("EmpReport deleted successfully");
      await dispatch(fetchEmpReportsAction());
      setLoading(false);
    }
  };

  const handleView = async (emp: EmpReport) => {
    setSelectedEmpReport(emp);
    setViewMode(true);
    setShowModal(true);
  };

  // --- Filter States ---
  const [filtered, setFiltered] = useState({
    project: "",
    task: "",
    fromDate: "",
    toDate: "",
  });

  const [filteredEmpReports, setFilteredEmpReports] = useState({
    project: "",
    task: "",
    fromDate: "",
    toDate: "",
  });

  // --- Default to true so ALL data shows on page load ---
  const [showResults, setShowResults] = useState(true);

  // Load filters from localStorage on mount (optional persistence)
  useEffect(() => {
    const storedFiltered = localStorage.getItem("EmployeeReportFilterUser");
    if (storedFiltered) {
      const parsedFiltered = JSON.parse(storedFiltered);
      setFiltered(parsedFiltered);
      setFilteredEmpReports(parsedFiltered);
      setShowResults(true);
    }
  }, []);

  const handleFilterChange = (field: string, value: string) => {
    setFiltered((prev) => ({ ...prev, [field]: value }));
  };

  const handleFindFilters = () => {
    const { project, fromDate, toDate, task } = filtered;

    // If user clicks "Find" with NO filters, show ALL data
    const noFiltersApplied = !project && !fromDate && !toDate && !task;
    if (noFiltersApplied) {
      localStorage.removeItem("EmployeeReportFilterUser");
      setFilteredEmpReports({
        project: "",
        task: "",
        fromDate: "",
        toDate: "",
      });
      setShowResults(true);
      toast("Showing all reports");
      return;
    }

    // If task is selected without project, show warning
    if (!project && (task || fromDate || toDate)) {
      toast("Please select at least one project before searching.");
      return;
    }

    // Apply filters
    localStorage.setItem("EmployeeReportFilterUser", JSON.stringify(filtered));
    setFilteredEmpReports(filtered);
    setShowResults(true);
  };

  const handleResetFilters = () => {
    localStorage.removeItem("EmployeeReportFilterUser");
    const resetStates = {
      fromDate: "",
      toDate: "",
      project: "",
      task: "",
    };
    setFiltered(resetStates);
    setFilteredEmpReports(resetStates);
    setShowResults(true);
  };

  const applyFiltered = useMemo(() => {
    if (!showResults) return [];

    const { fromDate, toDate, project, task } = filteredEmpReports;

    return empReportList.filter((emp) => {
      const matchesFromDate = fromDate
        ? new Date(emp.date) >= new Date(fromDate)
        : true;
      const matchesToDate = toDate
        ? new Date(emp.date) <= new Date(toDate)
        : true;
      const matchesProject = project
        ? emp.projectId?.toString() === project
        : true;
      const matchesTask = task ? emp.taskId?.toString() === task : true;

      return matchesFromDate && matchesToDate && matchesProject && matchesTask;
    });
  }, [filteredEmpReports, empReportList, showResults]);

  return (
    <Main>
      <PageContainer
        title="Daily Report"
        buttonText={loading ? "Loading..." : "ADD"}
        onButtonClick={() => setShowModal(true)}
      >
        <Modal
          visible={showModal}
          size="lg"
          onClose={() => {
            setShowModal(false);
            setSelectedEmpReport(null);
            setViewMode(false);
          }}
          headerText={selectedEmpReport ? "Update EmpReport" : "Add EmpReport"}
        >
          <>
            <EmployeeReportForm
              reportIdToEdit={selectedEmpReport?.id}
              onSave={addOrUpdateHandler}
              initialData={selectedEmpReport}
            />

            {viewMode && (
              <div className="d-flex justify-content-end mt-3">
                <button
                  className="btn btn-success"
                  onClick={() => setViewMode(false)}
                >
                  edit
                </button>
              </div>
            )}
          </>
        </Modal>

        <CollapseCard title="Daily Report Filter">
          <EmployeeReportFilter
            filters={filtered}
            onFind={handleFindFilters}
            onFilterChange={handleFilterChange}
            onReset={handleResetFilters}
          />
        </CollapseCard>

        <CollapseCard title="Daily Report List">
          {loading ? (
            <div className="text-center py-3">Loading reports...</div>
          ) : (
            <EmployeeReportList
              empReportList={applyFiltered}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onView={handleView}
            />
          )}
          
          {/* Empty state with type-safe conditional rendering */}
          {!loading && applyFiltered.length === 0 && showResults ? (
            <div className="text-center py-3 text-muted">
              No reports found matching the selected filters.
            </div>
          ) : <></>}
        </CollapseCard>
      </PageContainer>
    </Main>
  );
};

export default EmployeeReportPageUser;